<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { routePlansApi, type RoutePlan } from '@/api/route-plans';
import { type SerializedUser } from '@/api/users';
import { groupsApi } from '@/api/groups';
import { useGroupFilterStore } from '@/stores/group-filter';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import { getCareerColor } from '@/utils/careers';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useGroupFilterVisible } from '@/composables/useGroupFilterVisible';
import { useResponsiveTable } from '@/composables/useResponsiveTable';

const { t } = useI18n();

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 20 });

const currentUser = inject<Ref<SerializedUser>>('currentUser');
const groupsEnabled = computed(() => !!currentUser?.value?.companyGroupsEnabled);

const { accessibleCareers } = useAccessibleCareers();
const hasAccessibleCareer = computed(() => accessibleCareers.value.length > 0);

const { groupIds: groupFilterIds } = storeToRefs(useGroupFilterStore());

const { data: routePlans, isPending } = useQuery({
  queryKey: ['routePlans', groupFilterIds],
  queryFn: () => routePlansApi.index(groupFilterIds.value),
});

const { data: availableGroups, isPending: groupsLoading } = useQuery({
  queryKey: ['groups', 'forFilters'],
  queryFn: () => groupsApi.indexForFilters(),
  enabled: groupsEnabled,
  initialData: [],
});

const baseHeaders = [
  { title: t('routePlan.date'), value: 'humanDate', sortable: true },
  { title: t('routePlan.selectCareer'), value: 'humanCareer' },
  { title: t('routePlan.status'), value: 'status' },
  { title: t('routePlan.assignedVisitsLabel'), value: 'assignedCount' },
  { title: t('routePlan.caregiverCountLabel'), value: 'caregiverCount' },
  { title: t('routePlan.actions'), value: 'actions', sortable: false },
];

// Same rule as group-filter-select: a user who only sees one group gets no group column.
const { shouldShow: groupUiVisible } = useGroupFilterVisible();

const headers = computed(() => {
  if (!groupUiVisible.value) return baseHeaders;
  const groupCol = { title: t('routePlan.groups.label'), value: 'groupName', sortable: false };
  return [baseHeaders[0]!, groupCol, ...baseHeaders.slice(1)];
});

function statusColor(status: RoutePlan['status']) {
  const colors: Record<string, string> = {
    draft: 'grey',
    processing: 'info',
    completed: 'primary',
    failed: 'error',
    applied: 'success',
  };

  return colors[status] || 'grey';
}

function statusLabel(status: RoutePlan['status']) {
  const key = `routePlan.status${status.charAt(0).toUpperCase()}${status.slice(1)}` as keyof typeof t;

  return t(key) || status;
}

// Surface plans that finished with visits left out, so they get attention from the list.
function hasUnassigned(plan: RoutePlan) {
  return (plan.status === 'completed' || plan.status === 'applied') && (plan.unassignedCount ?? 0) > 0;
}

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const tomorrowStr = tomorrow.toISOString().split('T')[0] ?? '';

const createError = ref<string | null>(null);

const { mutate: createPlan, isPending: isCreating } = useMutation({
  mutationFn: (groupId: number | null) => routePlansApi.create({
    date: tomorrowStr,
    career: accessibleCareers.value[0]!.value,
    groupId,
    routePlanVisitsAttributes: [],
    routePlanCaregiversAttributes: [],
  }),
  onSuccess: (plan) => {
    window.location.href = `/route_plans/${plan.id}`;
  },
  onError: () => { createError.value = t('routePlan.optimizationFailed'); },
});

function resolveDefaultGroupId(): number | null {
  if (groupFilterIds.value.length === 1) return groupFilterIds.value[0]!;
  const groups = availableGroups.value ?? [];
  if (groups.length === 0) return null;
  return groups[0]!.id;
}

function startCreate() {
  if (!hasAccessibleCareer.value) {
    createError.value = t('routePlan.noAccessibleCareer');
    return;
  }
  const groupId = groupsEnabled.value ? resolveDefaultGroupId() : null;
  if (groupsEnabled.value && groupId === null) {
    createError.value = t('routePlan.groups.loading');
    return;
  }
  createPlan(groupId);
}
</script>

<template>
  <v-container class="v-col-12 v-col-md-10">
    <v-alert
      v-if="createError"
      type="error"
      closable
      class="mb-4"
      @click:close="createError = null"
    >
      {{ createError }}
    </v-alert>
    <div class="d-flex align-center justify-space-between mb-4 ga-3">
      <h1 class="text-h5">
        {{ t('routePlan.title') }}
      </h1>
      <div class="d-flex align-center ga-3">
        <group-filter-select
          v-model="groupFilterIds"
          variant="outlined"
          density="compact"
          hide-details
          class="list-group-filter"
        />
        <v-btn
          color="primary"
          :loading="isCreating || groupsLoading"
          :disabled="!hasAccessibleCareer"
          @click="startCreate"
        >
          <v-icon start>
            mdi-plus
          </v-icon>
          {{ t('routePlan.newOptimization') }}
        </v-btn>
      </div>
    </div>

    <v-card>
      <v-data-table
        v-bind="tableProps"
        :headers="headers"
        :header-props="{ class: 'font-weight-bold' }"
        :items="routePlans || []"
        :loading="isPending"
        class="elevation-0"
        :loading-text="t('routePlan.loading')"
      >
        <template #item.humanCareer="{ item }">
          <v-chip
            :color="getCareerColor(String(item.career))"
            size="small"
          >
            {{ item.humanCareer }}
          </v-chip>
        </template>
        <template #item.groupName="{ item }">
          <v-chip
            v-if="item.groupName"
            size="small"
            variant="tonal"
          >
            {{ item.groupName }}
          </v-chip>
          <span
            v-else
            class="text-medium-emphasis text-caption"
          >{{ t('routePlan.groups.none') }}</span>
        </template>
        <template #item.status="{ item }">
          <div class="d-flex flex-wrap align-center ga-1 py-1">
            <v-chip
              :color="statusColor(item.status)"
              size="small"
            >
              {{ statusLabel(item.status) }}
            </v-chip>
            <v-chip
              v-if="hasUnassigned(item)"
              color="warning"
              variant="tonal"
              size="small"
              prepend-icon="mdi-map-marker-alert-outline"
            >
              {{ t('routePlan.unassigned.countChip', { count: item.unassignedCount }) }}
            </v-chip>
          </div>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            icon="mdi-eye"
            size="small"
            variant="text"
            :href="`/route_plans/${item.id}`"
          />
        </template>
        <template #no-data>
          <div class="text-center pa-8 text-medium-emphasis">
            <v-icon
              size="48"
              class="mb-2"
            >
              mdi-routes
            </v-icon>
            <p>{{ t('routePlan.emptyState') }}</p>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.list-group-filter {
  min-width: 220px;
  max-width: 280px;
}
</style>
